// API client for backend communication
import { localStore } from './local-store';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const REQUEST_TIMEOUT_MS = 15000; // 15 seconds

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

class ApiClient {
  private baseUrl: string;
  private authToken: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    // Eagerly restore token from localStorage to avoid race with auth context useEffect
    if (typeof window !== 'undefined') {
      this.authToken = localStorage.getItem('auth_token');
    }
  }

  setAuthToken(token: string | null) {
    this.authToken = token;
  }

  private isNetworkError(error: unknown): boolean {
    return error instanceof TypeError &&
      (error.message.includes('fetch') ||
       error.message.includes('Failed to fetch') ||
       error.message.includes('NetworkError') ||
       error.message.includes('Network request failed') ||
       error.message.includes('Load failed'));
  }

  private async delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount: number = 0,
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      ...options.headers as Record<string, string>,
    };

    // Only set Content-Type for requests with a string body (JSON).
    // FormData bodies need the browser to auto-set the multipart boundary.
    if (typeof options.body === 'string' || (!options.body && options.method !== 'GET')) {
      headers['Content-Type'] = 'application/json';
    }

    // Add auth token if available
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      if (!response.ok) {
        // Handle 401 Unauthorized (token expired/invalid)
        if (response.status === 401) {
          this.authToken = null;
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          window.dispatchEvent(new Event('auth:unauthorized'));
        }

        // 403 Forbidden = permission denied (don't logout, just throw error)

        let errorData;
        try {
          const text = await response.text();
          if (text) {
            errorData = JSON.parse(text);
          } else {
            errorData = {
              message: `HTTP error! status: ${response.status}`,
              status: response.status,
              statusText: response.statusText,
            };
          }
        } catch {
          errorData = {
            message: `HTTP error! status: ${response.status}`,
            status: response.status,
            statusText: response.statusText,
          };
        }
        
        // Handle nested NestJS error format: { message: { message: "...", error: "...", statusCode: 409 } }
        let errorMessage = errorData.message;
        if (typeof errorMessage === 'object' && errorMessage !== null) {
          errorMessage = errorMessage.message || errorMessage.error || JSON.stringify(errorMessage);
        }
        errorMessage = errorMessage || errorData.error;
        if (!errorMessage) {
          if (response.status === 404) {
            errorMessage = `Route not found: ${endpoint}. Make sure the backend is running and the route exists.`;
          } else {
            errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          }
        }
        throw new ApiError(errorMessage, response.status);
      }

      // Handle empty responses
      const text = await response.text();
      if (!text) {
        return null as T;
      }

      return JSON.parse(text);
    } catch (error) {
      // Treat timeout aborts as network errors
      const isTimeout = error instanceof DOMException && error.name === 'AbortError';
      const isNetwork = this.isNetworkError(error) || isTimeout;

      // Retry on network errors for idempotent methods only (GET/HEAD/OPTIONS)
      const method = (options.method || 'GET').toUpperCase();
      const isIdempotent = ['GET', 'HEAD', 'OPTIONS'].includes(method);
      if (isNetwork && isIdempotent && retryCount < MAX_RETRIES) {
        const retryDelay = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
        await this.delay(retryDelay);
        return this.request<T>(endpoint, options, retryCount + 1);
      }

      // Final failure after retries or non-network error
      if (isTimeout) {
        throw new Error(
          `Request timed out after ${REQUEST_TIMEOUT_MS / 1000}s. ` +
          `Please check your network connection and try again.`
        );
      }
      if (this.isNetworkError(error)) {
        throw new Error(
          `Failed to connect to backend after ${retryCount} retries. ` +
          `Please check your network connection and try again.`
        );
      }

      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async requestBlob(endpoint: string): Promise<Blob> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {};
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch { /* use default */ }
      throw new ApiError(errorMessage, response.status);
    }

    return response.blob();
  }

  // Generic HTTP methods for flexibility
  async get<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async patch<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // Products (server-side pagination)
  async getProductsPaginated(options: {
    limit: number;
    offset: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<{ items: any[]; total: number }> {
    const params = new URLSearchParams();
    params.append('limit', options.limit.toString());
    params.append('offset', options.offset.toString());
    if (options.search) params.append('search', options.search);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    return this.request<{ items: any[]; total: number }>(`/products?${params.toString()}`);
  }

  async searchProducts(query: string, limit: number = 20): Promise<any[]> {
    if (!query || query.trim().length === 0) return [];
    return this.request<any[]>(`/products/search?q=${encodeURIComponent(query.trim())}&limit=${limit}`);
  }

  async createProduct(data: any) {
    return this.request<any>('/products', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProduct(id: string, _organizationId: string, data: any) {
    return this.request<any>(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProduct(id: string, _organizationId?: string) {
    return this.request<void>(`/products/${id}`, {
      method: 'DELETE',
    });
  }

  // Product Attribute Definitions
  async getProductAttributeDefinitions() {
    return this.request<import('@/types/inventory').ProductAttributeDefinition[]>(
      '/product-attribute-definitions'
    );
  }

  async createProductAttributeDefinition(data: {
    name: string;
    key: string;
    fieldType: 'TEXT' | 'NUMBER' | 'SELECT';
    isRequired?: boolean;
    sortOrder?: number;
    options?: string[];
    defaultValue?: string | null;
  }) {
    return this.request<import('@/types/inventory').ProductAttributeDefinition>(
      '/product-attribute-definitions',
      { method: 'POST', body: JSON.stringify(data) }
    );
  }

  async updateProductAttributeDefinition(id: string, data: {
    name?: string;
    fieldType?: 'TEXT' | 'NUMBER' | 'SELECT';
    isRequired?: boolean;
    isActive?: boolean;
    sortOrder?: number;
    options?: string[];
    defaultValue?: string | null;
  }) {
    return this.request<import('@/types/inventory').ProductAttributeDefinition>(
      `/product-attribute-definitions/${id}`,
      { method: 'PUT', body: JSON.stringify(data) }
    );
  }

  async deleteProductAttributeDefinition(id: string) {
    return this.request<void>(
      `/product-attribute-definitions/${id}`,
      { method: 'DELETE' }
    );
  }

  // Attribute Suggestions
  async getAttributeSuggestions(query?: string, limit?: number) {
    const params = new URLSearchParams();
    if (query) params.append('query', query);
    if (limit) params.append('limit', limit.toString());
    const queryString = params.toString();
    return this.request<Array<{ id: number; attributeName: string; usageCount: number }>>(
      `/products/attributes/suggestions${queryString ? `?${queryString}` : ''}`
    );
  }

  async getAttributeValueSuggestions(attributeName: string, query?: string, limit?: number) {
    const params = new URLSearchParams();
    if (query) params.append('query', query);
    if (limit) params.append('limit', limit.toString());
    const queryString = params.toString();
    return this.request<Array<{ id: number; value: string; usageCount: number }>>(
      `/products/attributes/${encodeURIComponent(attributeName)}/values/suggestions${queryString ? `?${queryString}` : ''}`
    );
  }

  // Product Import
  async getProductImportFields() {
    return this.request<ProductImportFieldConfig[]>('/products/import/fields');
  }

  async parseProductImportFile(file: File): Promise<ProductImportParseResult> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.baseUrl}/products/import/parse`;
    const headers: Record<string, string> = {};
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || `Upload failed: ${response.statusText}`);
    }

    return response.json();
  }

  async validateProductImport(data: {
    fileId: string;
    columnMappings: Record<string, string>;
    defaultValues?: Record<string, any>;
    valueTransformations?: Record<string, Record<string, string>>;
  }) {
    return this.request<ProductImportValidationResult>('/products/import/validate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async executeProductImport(data: {
    fileId: string;
    columnMappings: Record<string, string>;
    defaultValues?: Record<string, any>;
    valueTransformations?: Record<string, Record<string, string>>;
  }) {
    return this.request<ProductImportResult>('/products/import/execute', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Product Import Templates
  async getProductImportTemplates() {
    return this.request<ProductImportTemplate[]>('/products/import/templates');
  }

  async createProductImportTemplate(data: {
    name: string;
    description?: string;
    columnMappings: Record<string, string>;
    defaultValues?: Record<string, any>;
    valueTransformations?: Record<string, Record<string, string>>;
    isDefault?: boolean;
  }) {
    return this.request<ProductImportTemplate>('/products/import/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Inbound Management
  async getInboundOrders(options?: {
    inboundType?: 'PURCHASE' | 'PRODUCTION' | 'DIRECT_RECEIPT' | 'RETURN';
    excludeInboundType?: 'PURCHASE' | 'PRODUCTION' | 'DIRECT_RECEIPT' | 'RETURN';
    warehouseId?: string;
    statuses?: string[];
    search?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    const params = new URLSearchParams();
    if (options?.inboundType) params.set('inboundType', options.inboundType);
    if (options?.excludeInboundType) params.set('excludeInboundType', options.excludeInboundType);
    if (options?.warehouseId) params.set('warehouseId', options.warehouseId);
    if (options?.statuses?.length) params.set('statuses', options.statuses.join(','));
    if (options?.search) params.set('search', options.search);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset != null) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      items: any[];
      total: number;
      limit: number;
      offset: number;
    }>(`/inbound-orders${query}`);
  }

  async getInboundOrder(id: string) {
    return this.request<any>(`/inbound-orders/${id}`);
  }

  async getInboundOrderItems(id: string) {
    return this.request<Array<{
      id: string;
      productId: string;
      product: { id: string; catalogNumber: string; name: string };
      uom: string;
      quantityExpected: number;
      quantityReceived: number;
      quantityPrinted: number;
      quantityPutaway: number;
      batchNo?: string;
      expiryDate?: string;
      originalInventoryItemId?: string | null;
      skipPrint: boolean;
    }>>(`/inbound-orders/${id}/items`);
  }

  async getInboundInventoryItems(id: string) {
    return this.request<Array<{
      id: string;
      inboundItemId: string;
      epc: string | null;
      serialNumber: string | null;
      catalogNumber: string | null;
      productName: string | null;
      batchNo: string | null;
      expiryDate: string | null;
      putawayDate: string | null;
      putawayBy: string | null;
      location: {
        zone: string | null;
        aisle: string | null;
        rack: string | null;
        level: string | null;
        bin: string | null;
      } | null;
    }>>(`/inbound-orders/${id}/inventory-items`);
  }

  async getReturnableItems(outboundOrderId: string) {
    return this.request<Array<{
      productId: string;
      productName: string;
      catalogNumber: string;
      uom: string;
      quantityOrdered: number;
      quantityReturned: number;
      returnableQuantity: number;
    }>>(`/inbound-orders/returnable-items/${outboundOrderId}`);
  }

  async getReturnableInventoryItems(outboundOrderId: string, excludeOrderId?: string) {
    const query = excludeOrderId ? `?excludeOrderId=${encodeURIComponent(excludeOrderId)}` : '';
    return this.request<Array<{
      productId: string;
      catalogNumber: string;
      productName: string;
      totalItemCount: number;
      batches: Array<{
        batchId: string | null;
        batchNo: string | null;
        expiryDate: string | null;
        itemCount: number;
        items: Array<{
          id: string;
          serialNumber: string | null;
          epc: string;
          status: string;
        }>;
      }>;
    }>>(`/inbound-orders/returnable-inventory/${outboundOrderId}${query}`);
  }

  async createInboundOrder(data: {
    inboundType?: string;
    referenceNumber?: string;
    supplierId?: string;
    reasonCodeId?: string;
    outboundOrderId?: string;
    warehouseId?: string;
    expectedArrivalDate?: string;
    items: Array<{
      productId: string;
      uom: string;
      quantityExpected: number;
      batchNo?: string;
      expiryDate?: string;
      originalInventoryItemId?: string;
      skipPrint?: boolean;
    }>;
  }) {
    return this.request<{ id: string; orderNumber: string }>('/inbound-orders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async addInboundItem(inboundOrderId: string, data: {
    productId: string;
    uom: string;
    quantityExpected: number;
    batchNo?: string;
    expiryDate?: string;
    originalInventoryItemId?: string;
    skipPrint?: boolean;
  }) {
    return this.request<{ id: string }>(`/inbound-orders/${inboundOrderId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInboundItem(inboundOrderId: string, itemId: string, data: {
    quantityExpected?: number;
    batchNo?: string | null;
    expiryDate?: string | null;
    skipPrint?: boolean;
  }) {
    return this.request<{ id: string }>(`/inbound-orders/${inboundOrderId}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteInboundItem(inboundOrderId: string, itemId: string) {
    return this.request<any>(`/inbound-orders/${inboundOrderId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  async validateInboundOrder(id: string, batchData?: {
    batchNumber?: string;
    expiryDate?: string;
    items?: Array<{
      itemId: string;
      batchNumber?: string;
      expiryDate?: string;
    }>;
  }) {
    return this.request<{ order: any; itemsGenerated: number }>(`/inbound-orders/${id}/validate`, {
      method: 'POST',
      body: JSON.stringify(batchData || {}),
    });
  }

  async assignInboundOrder(id: string, userId: string) {
    return this.request<any>(`/inbound-orders/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async releaseInboundOrder(id: string) {
    return this.request<any>(`/inbound-orders/${id}/release`, {
      method: 'POST',
    });
  }

  // Warehouses
  async getWarehouses() {
    return this.request<any[]>('/warehouses');
  }

  async createWarehouse(data: any) {
    return this.request<any>('/warehouses', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWarehouse(id: string, data: any) {
    return this.request<any>(`/warehouses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Locations
  // Get all locations with occupancy counts (for tree visualization)
  async getLocationsWithOccupancy(warehouseId?: string) {
    const query = warehouseId ? `?warehouseId=${warehouseId}` : '';
    return this.request<{
      locations: any[];
      occupancy: Record<string, number>;
    }>(`/locations/with-occupancy${query}`);
  }

  async createLocation(warehouseId: string, data: any) {
    return this.request<any>(`/locations`, {
      method: 'POST',
      body: JSON.stringify({ ...data, warehouseId }),
    });
  }

  async deleteLocation(locationId: string) {
    return this.request<{ success: boolean; message: string; deletedCount: number }>(`/locations/${locationId}`, {
      method: 'DELETE',
    });
  }

  async moveLocation(locationId: string, destinationId: string) {
    return this.request<any>(`/locations/${locationId}/move`, {
      method: 'PUT',
      body: JSON.stringify({ destinationId }),
    });
  }

  async getLocationInventory(locationId: string) {
    return this.request<{
      location: {
        id: string;
        warehouse: { code: string; name: string };
        zone: { code: string | null; name: string | null };
        aisle: string | null;
        rack: string | null;
        level: string | null;
        bin: string | null;
      };
      totalItems: number;
      products: Array<{
        productId: string;
        catalogNumber: string;
        name: string;
        totalQty: number;
        batchCount: number;
        statusBreakdown: Record<string, number>;
        batches: Array<{
          batchId: string | null;
          batchNo: string | null;
          qty: number;
          expiryDate: string | null;
          productionDate: string | null;
          items: Array<{
            id: string;
            epc: string | null;
            serialNumber: string | null;
          }>;
        }>;
      }>;
    }>(`/locations/${locationId}/inventory`);
  }

  // Auth & Registration
  async login(identifier: string, password: string) {
    return this.request<{ accessToken: string; user: any }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier, password }),
    });
  }

  async getCurrentUser() {
    return this.request<any>('/auth/me');
  }

  async getAvailableWarehouses() {
    return this.request<any[]>('/auth/warehouses');
  }

  async registerOrganization(data: {
    organizationName: string;
    ownerEmail: string;
    ownerName: string;
  }) {
    return this.request<{ message: string; organizationId: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async approveOrganization(organizationId: string) {
    return this.request<{ message: string; organization: any; resetLink?: string; ownerEmail?: string }>(`/auth/approve/${organizationId}`, {
      method: 'PUT',
      body: JSON.stringify({ organizationId }),
    });
  }

  // Suppliers
  async getSuppliers(options?: { search?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.limit) params.set('limit', options.limit.toString());
    const query = params.toString();
    return this.request<any[]>(`/suppliers${query ? `?${query}` : ''}`);
  }

  async searchSuppliers(query: string, limit = 10) {
    return this.getSuppliers({ search: query, limit });
  }

  async createSupplier(data: any) {
    return this.request<any>('/suppliers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSupplier(id: string, data: any) {
    return this.request<any>(`/suppliers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSupplier(id: string) {
    return this.request<void>(`/suppliers/${id}`, {
      method: 'DELETE',
    });
  }

  // Customers
  async getCustomers() {
    return this.request<any[]>('/customers');
  }

  async searchCustomers(query: string, limit = 10) {
    // Client-side search since backend doesn't have search endpoint yet
    const customers = await this.getCustomers();
    const lowerQuery = query.toLowerCase();
    return customers
      .filter((c: any) =>
        c.code?.toLowerCase().includes(lowerQuery) ||
        c.name?.toLowerCase().includes(lowerQuery)
      )
      .slice(0, limit);
  }

  async createCustomer(data: {
    name: string;
    contactPerson?: string;
    email?: string;
    phone?: string;
    addressStreet?: string;
    addressCity?: string;
    addressState?: string;
    addressZip?: string;
    addressCountry?: string;
  }) {
    return this.request<any>('/customers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCustomer(id: string, data: {
    name?: string;
    contactPerson?: string;
    email?: string;
    phone?: string;
    addressStreet?: string;
    addressCity?: string;
    addressState?: string;
    addressZip?: string;
    addressCountry?: string;
  }) {
    return this.request<any>(`/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCustomer(id: string) {
    return this.request<void>(`/customers/${id}`, {
      method: 'DELETE',
    });
  }

  // Reason Codes
  async getReasonCodes(scope?: 'INBOUND' | 'OUTBOUND' | 'ADJUSTMENT' | 'ALL') {
    const query = scope ? `?scope=${scope}` : '';
    return this.request<any[]>(`/reason-codes${query}`);
  }

  async createReasonCode(data: {
    code: string;
    description?: string;
    usageScope: 'INBOUND' | 'OUTBOUND' | 'ADJUSTMENT' | 'ALL';
    reasonType?: 'INCREASE' | 'DECREASE' | 'BOTH';
  }) {
    return this.request<any>('/reason-codes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateReasonCode(id: string, data: {
    code?: string;
    description?: string;
    usageScope?: 'INBOUND' | 'OUTBOUND' | 'ADJUSTMENT' | 'ALL';
    reasonType?: 'INCREASE' | 'DECREASE' | 'BOTH';
    isActive?: boolean;
  }) {
    return this.request<any>(`/reason-codes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteReasonCode(id: string) {
    return this.request<void>(`/reason-codes/${id}`, {
      method: 'DELETE',
    });
  }

  async completeInbound(inboundOrderId: string, notes?: string) {
    return this.request<{ order: unknown; voidedItemCount: number }>(
      `/inbound-orders/${inboundOrderId}/complete`,
      {
        method: 'POST',
        body: JSON.stringify({ notes }),
      },
    );
  }

  async cancelInbound(inboundOrderId: string) {
    return this.request<{ order: any; deletedItemCount: number }>(
      `/inbound-orders/${inboundOrderId}/cancel-inbound`,
      { method: 'POST' },
    );
  }

  // Organizations (for admin dashboard)
  async getOrganizations() {
    return this.request<any[]>('/organizations');
  }

  async getOrganization(id: string) {
    return this.request<any>(`/organizations/${id}`);
  }

  async getOrganizationDetails(id: string) {
    return this.request<{
      organization: any;
      members: Array<{
        id: string;
        userId: string;
        roleId: string | null;
        isActive: boolean;
        user: {
          id: string;
          email: string | null;
          fullName: string | null;
        };
        role: {
          id: string;
          name: string;
        } | null;
      }>;
    }>(`/organizations/${id}/details`);
  }

  async resetPassword(token: string, password: string) {
    return this.request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  // Admin utilities
  async promoteToAdmin(email: string) {
    return this.request<{ message: string; user: any }>('/auth/promote-to-admin', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async updateOrganization(id: string, data: Partial<any>) {
    return this.request<any>(`/organizations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteOrganization(id: string) {
    return this.request<void>(`/organizations/${id}`, {
      method: 'DELETE',
    });
  }

  async resetOrgDataForTesting(orgId: string) {
    return this.request<{ message: string; counts: any }>(`/auth/reset-org-data-for-testing/${orgId}`, {
      method: 'POST',
    });
  }

  async resetOperationalData(orgId: string) {
    return this.request<{ message: string; counts: any }>(`/auth/reset-operational-data/${orgId}`, {
      method: 'POST',
    });
  }

  // Database Backup
  async listBackups(): Promise<Array<{ key: string; timestamp: string; sizeBytes: number; pgVersion: string; type: 'manual' | 'predeploy' | 'scheduled' }>> {
    return this.get('/database-backup/list');
  }

  async createBackup(): Promise<{ key: string; timestamp: string; sizeBytes: number; pgVersion: string; type: string }> {
    return this.post('/database-backup/create');
  }

  async downloadBackupByKey(key: string): Promise<Blob> {
    // key is like "backups/manual-2024-01-01T00-00-00-000Z.enc" — strip "backups/" prefix for the URL
    const urlKey = key.startsWith('backups/') ? key.slice('backups/'.length) : key;
    return this.requestBlob(`/database-backup/download/${urlKey}`);
  }

  async deleteBackup(key: string): Promise<{ message: string }> {
    const urlKey = key.startsWith('backups/') ? key.slice('backups/'.length) : key;
    return this.delete(`/database-backup/${urlKey}`);
  }

  async restoreBackupFromKey(key: string): Promise<{ message: string }> {
    return this.post('/database-backup/restore', { key });
  }

  /** @deprecated Use createBackup() + downloadBackupByKey() instead */
  async downloadBackup(): Promise<Blob> {
    return this.requestBlob('/database-backup/download');
  }

  async restoreBackup(file: File): Promise<{ message: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.baseUrl}/database-backup/restore`;
    const headers: Record<string, string> = {};
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || `Restore failed: ${response.statusText}`);
    }

    return response.json();
  }

  // Operator Management
  async generateBadgePdf(userId: string): Promise<Blob> {
    const url = `${this.baseUrl}/auth/badges/${userId}/pdf`;
    const headers: Record<string, string> = {};
    
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: `HTTP error! status: ${response.status}` };
      }
      throw new Error(errorData.message || `Failed to generate badge: ${response.status}`);
    }

    return response.blob();
  }

  async generateBadgeImage(userId: string): Promise<Blob> {
    const url = `${this.baseUrl}/auth/badges/${userId}/image`;
    const headers: Record<string, string> = {};
    
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: `HTTP error! status: ${response.status}` };
      }
      throw new Error(errorData.message || `Failed to generate badge: ${response.status}`);
    }

    return response.blob();
  }

  // Users
  async getUsers() {
    return this.request<any[]>('/auth/users');
  }

  async createUser(data: {
    fullName: string;
    username?: string;
    password?: string;
    email?: string;
    roleId?: string;
    warehouseId?: string;
  }) {
    return this.request<{
      user: any;
      username: string;
      badgeToken: string;
      resetLink?: string;
    }>('/auth/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // User Warehouse Assignments
  async setUserWarehouseAssignments(
    userId: string,
    warehouseIds: string[],
    defaultWarehouseId?: string,
  ) {
    return this.request<any[]>(`/auth/users/${userId}/warehouses`, {
      method: 'PUT',
      body: JSON.stringify({ warehouseIds, defaultWarehouseId }),
    });
  }

  async removeUserWarehouseAssignment(userId: string, warehouseId: string) {
    return this.request<any>(`/auth/users/${userId}/warehouses/${warehouseId}`, {
      method: 'DELETE',
    });
  }

  async setUserDefaultWarehouse(userId: string, warehouseId: string) {
    return this.request<any>(`/auth/users/${userId}/warehouses/${warehouseId}/default`, {
      method: 'PUT',
    });
  }

  // User Management
  async updateUser(userId: string, data: { fullName?: string; status?: string; username?: string; email?: string; password?: string }) {
    return this.request<any>(`/auth/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async assignUserRole(userId: string, roleId: string | null) {
    return this.request<any>(`/auth/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ roleId }),
    });
  }

  // Roles
  async getRoles() {
    return this.request<any[]>('/auth/roles');
  }

  async createRole(data: { name: string; description?: string }) {
    return this.request<any>('/auth/roles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRole(roleId: string, data: { name?: string; description?: string }) {
    return this.request<any>(`/auth/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRole(roleId: string) {
    return this.request<any>(`/auth/roles/${roleId}`, {
      method: 'DELETE',
    });
  }

  // Permissions
  async getPermissions() {
    return this.request<{ slug: string; description: string | null; groupName: string | null; forWeb: boolean; forMobile: boolean }[]>('/auth/permissions');
  }

  async getRolePermissions(roleId: string) {
    return this.request<{ web: string[]; mobile: string[] }>(`/auth/roles/${roleId}/permissions`);
  }

  async setRolePermissions(roleId: string, web: string[], mobile: string[]) {
    return this.request<{ web: string[]; mobile: string[] }>(`/auth/roles/${roleId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ web, mobile }),
    });
  }

  // Facilities
  async getFacilities() {
    return this.request<any[]>('/facilities');
  }

  async createFacility(data: {
    code: string;
    name: string;
    addressStreet?: string;
    addressCity?: string;
    addressState?: string;
    addressZip?: string;
    addressCountry?: string;
  }) {
    return this.request<any>('/facilities', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFacility(id: string, data: Partial<{
    code: string;
    name: string;
    addressStreet?: string;
    addressCity?: string;
    addressState?: string;
    addressZip?: string;
    addressCountry?: string;
    isActive?: boolean;
  }>) {
    return this.request<any>(`/facilities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteFacility(id: string) {
    return this.request<any>(`/facilities/${id}`, {
      method: 'DELETE',
    });
  }

  // Outbound Orders
  async getOutboundOrders(options?: {
    limit?: number;
    offset?: number;
    statuses?: string[];
    warehouseId?: string;
    includeItems?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
  }) {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset != null) params.append('offset', options.offset.toString());
    if (options?.statuses?.length) params.append('statuses', options.statuses.join(','));
    if (options?.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options?.includeItems) params.append('includeItems', 'true');
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.search) params.append('search', options.search);
    const query = params.toString();
    return this.request<{
      items: any[];
      total: number;
      limit: number;
      offset: number;
    }>(`/outbound-orders${query ? `?${query}` : ''}`);
  }

  async getOutboundOrderAllocatedItems(orderId: string) {
    return this.request<{
      allocatedItems: Array<{
        id: string;
        identifier: string;
        inventoryItemId: string;
        productId: string;
        productName: string;
        catalogNumber: string;
        epc: string | null;
        serialNumber: string | null;
        batchId: string | null;
        batchNo: string | null;
        expiryDate: string | null;
        allocatedAt: string;
      }>;
      totalAllocated: number;
    }>(`/outbound-orders/${orderId}/allocated-items`);
  }

  async startPicking(orderId: string, force = false) {
    return this.post<{
      success: boolean;
      shortages?: Array<{
        productId: string;
        productName: string;
        catalogNumber: string;
        required: number;
        available: number;
      }>;
    }>(`/outbound-orders/${orderId}/start-picking${force ? '?force=true' : ''}`);
  }

  async createOutboundOrder(data: {
    orderNumber?: string;
    referenceNumber?: string;
    warehouseId: string;
    customerId?: string;
    requestedDeliveryDate?: string;
    notes?: string;
    items: Array<{
      productId: string;
      quantity: number;
    }>;
  }) {
    return this.request<{ order: any }>('/outbound-orders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getOutboundOrder(id: string) {
    return this.request<any>(`/outbound-orders/${id}`);
  }

  async getOutboundOrderItems(orderId: string) {
    return this.request<Array<{
      id: string;
      productId: string;
      uom: string;
      quantityOrdered: number;
      quantityPicked: number;
      quantityShipped: number;
      product?: { id: string; name: string; catalogNumber: string } | null;
    }>>(`/outbound-orders/${orderId}/items`);
  }

  async updateOutboundOrder(id: string, data: {
    referenceNumber?: string | null;
    customerId?: string | null;
    requestedDeliveryDate?: string | null;
    notes?: string | null;
  }) {
    return this.request<any>(`/outbound-orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async addOutboundItem(orderId: string, data: { productId: string; quantity: number; uom?: string }) {
    return this.request<any>(`/outbound-orders/${orderId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOutboundItem(orderId: string, itemId: string, data: { quantity: number }) {
    return this.request<any>(`/outbound-orders/${orderId}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteOutboundItem(orderId: string, itemId: string) {
    return this.request<any>(`/outbound-orders/${orderId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  async getStockList(options?: {
    warehouseId?: string;
    facilityId?: string;
    search?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    showExpired?: boolean;
    statuses?: string[];
  }) {
    const params = new URLSearchParams();
    if (options?.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options?.facilityId) params.append('facilityId', options.facilityId);
    if (options?.search) params.append('search', options.search);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset != null) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.showExpired) params.append('showExpired', 'true');
    if (options?.statuses) params.append('statuses', options.statuses.join(','));
    const query = params.toString() ? `?${params.toString()}` : '';

    return this.request<{
      items: Array<{
        productId: string;
        catalogNumber: string;
        name: string;
        type: string;
        baseUom: string;
        category: string | null;
        totalOnHand: number;
        receivedQty: number;
        availableQty: number;
        allocatedQty: number;
        expiredQty: number;
        batchCount: number;
        reorderPoint: number;
        minStockLevel: number;
        componentProductId: string | null;
        unitsPerPack: number | null;
        stockStatus: 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'CRITICAL';
        batches: Array<{
          batchId: string | null;
          batchNo: string | null;
          quantity: number;
          availableQty: number;
          productionDate: string | null;
          expiryDate: string | null;
          daysUntilExpiry: number | null;
          locationId: string | null;
          items: Array<{
            id: string;
            epc: string;
            serialNumber: string | null;
            status: string;
            quantity: number;
            locationId: string | null;
            location: {
              zone: string | null;
              aisle: string | null;
              rack: string | null;
              level: string | null;
              bin: string | null;
            } | null;
            outboundOrderNumber: string | null;
            outboundOrderId: string | null;
          }>;
        }>;
      }>;
      total: number;
      totalOnHand: number;
      totalExpired: number;
      limit: number;
      offset: number;
    }>(`/stock-list${query}`);
  }

  async getProductItems(productId: string, options?: {
    warehouseId?: string;
    facilityId?: string;
    showExpired?: boolean;
    statuses?: string[];
  }): Promise<Array<{
    id: string;
    epc: string;
    serialNumber: string | null;
    status: string;
    quantity: number;
    locationId: string | null;
    location: {
      zone: string | null;
      aisle: string | null;
      rack: string | null;
      level: string | null;
      bin: string | null;
    } | null;
    batchNo: string | null;
    expiryDate: string | null;
    outboundOrderNumber: string | null;
    outboundOrderId: string | null;
  }>> {
    const params = new URLSearchParams();
    if (options?.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options?.facilityId) params.append('facilityId', options.facilityId);
    if (options?.showExpired) params.append('showExpired', 'true');
    if (options?.statuses) params.append('statuses', options.statuses.join(','));
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/stock-list/product-items/${productId}${query}`);
  }

  // ============================================================================
  // LABEL TEMPLATES (offline cached for printing)
  // ============================================================================

  // Sync label templates to local cache
  async syncLabelTemplates(): Promise<void> {
    if (!this.authToken) {
      throw new Error('Auth token required for sync');
    }
    return localStore.syncLabelTemplates(this.baseUrl, this.authToken);
  }

  // ============================================================================
  // STOCK TAKE (uses stock_takes, stock_take_scans, stock_take_results tables)
  // ============================================================================

  async getStockTakes(options?: { statuses?: string[]; warehouseId?: string; limit?: number; offset?: number; sortBy?: string; sortOrder?: 'asc' | 'desc'; search?: string }) {
    const params = new URLSearchParams();
    if (options?.statuses?.length) params.append('statuses', options.statuses.join(','));
    if (options?.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset != null) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.search) params.append('search', options.search);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ items: StockTakeTask[]; total: number; limit: number; offset: number }>(`/stock-takes${query}`);
  }

  async completeStockTake(id: string) {
    return this.request<StockTakeTask>(`/stock-takes/${id}/complete`, {
      method: 'POST',
    });
  }

  async cancelStockTake(id: string) {
    return this.request<StockTakeTask>(`/stock-takes/${id}/cancel`, {
      method: 'POST',
    });
  }

  async getStockTakeScannedItems(id: string, limit?: number, offset?: number, locationId?: string) {
    const params = new URLSearchParams();
    if (limit != null) params.set('limit', limit.toString());
    if (offset != null) params.set('offset', offset.toString());
    if (locationId != null) params.set('locationId', locationId);
    const qs = params.toString();
    return this.request<{
      items: Array<{
        id: string;
        epc: string | null;
        serialNumber: string | null;
        catalogNumber: string | null;
        productName: string | null;
        batchNo: string | null;
        expiryDate: string | null;
        status: string;
        scanned: boolean;
        expected: boolean;
        location: {
          zone: string | null;
          aisle: string | null;
          rack: string | null;
          level: string | null;
          bin: string | null;
        } | null;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(`/stock-takes/${id}/scanned-items${qs ? `?${qs}` : ''}`);
  }

  async getAllStockTakeScannedItems(id: string, locationId?: string) {
    const allItems: Awaited<ReturnType<typeof this.getStockTakeScannedItems>>['items'] = [];
    let offset = 0;
    const limit = 500;

    while (true) {
      const response = await this.getStockTakeScannedItems(id, limit, offset, locationId);
      allItems.push(...response.items);
      offset += response.items.length;
      if (offset >= response.total) break;
    }

    return allItems;
  }

  async getStockTakeLocationProgress(id: string) {
    return this.request<{
      locations: Array<{
        locationId: string | null;
        locationCode: string;
        locationType: string | null;
        zone: string | null;
        aisle: string | null;
        rack: string | null;
        level: string | null;
        bin: string | null;
        expectedCount: number;
        foundCount: number;
        missingCount: number;
        extraCount: number;
      }>;
    }>(`/stock-takes/${id}/location-progress`);
  }

  // ============================================================================
  // INVENTORY VARIANCES & SIGHTINGS
  // ============================================================================

  async getMissingItems(options?: {
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
    warehouseId?: string;
  }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset != null) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.search) params.set('search', options.search);
    if (options?.warehouseId) params.set('warehouseId', options.warehouseId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      items: Array<{
        id: string;
        epc: string | null;
        serialNumber: string | null;
        status: string;
        missingSince: string | null;
        product?: { id: string; name: string; catalogNumber: string | null } | null;
        lastKnownLocation?: { id: string; code: string } | null;
        lastSeenAt?: string | null;
        lastSeenNear?: { id: string; zone?: string | null; aisle?: string | null; rack?: string | null; level?: string | null; bin?: string | null } | null;
        lastSeenBy?: { id: string; name: string | null } | null;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(`/inventory/items/missing${query}`);
  }

  async breakdownBulkItem(itemId: string, warehouseId: string) {
    return this.request<{ itemId: string; childCount: number }>(`/inventory/items/${itemId}/breakdown`, {
      method: 'POST',
      body: JSON.stringify({ warehouseId }),
    });
  }

  async recoverMissingItem(itemId: string, locationId: string, notes?: string) {
    return this.request<{ success: boolean; message?: string }>(
      `/inventory/items/${itemId}/recover`,
      {
        method: 'POST',
        body: JSON.stringify({ locationId, notes }),
      }
    );
  }

  async getInventoryItemHistory(itemId: string) {
    return this.request<{
      item: {
        id: string;
        epc: string | null;
        serialNumber: string | null;
        status: string;
        batchNo: string | null;
        expiryDate: string | null;
        createdAt: string;
        updatedAt: string;
        product: { id: string; catalogNumber: string; name: string } | null;
        location: { id: string; zone: string | null; aisle: string | null; rack: string | null; level: string | null; bin: string | null } | null;
      };
      inboundOrder: {
        id: string;
        orderNumber: string;
        status: string;
      } | null;
      outboundOrder: {
        id: string;
        orderNumber: string;
        status: string;
      } | null;
      transactions: Array<{
        id: string;
        transactionType: string;
        referenceId: string | null;
        referenceType: string | null;
        quantityChange: string;
        notes: string | null;
        timestamp: string;
        fromLocationId: string | null;
        toLocationId: string | null;
        user: { id: string; username: string | null; fullName: string | null } | null;
      }>;
      printHistory: Array<{
        id: string;
        status: string;
        printerName: string | null;
        printerAddress: string | null;
        errorMessage: string | null;
        printedAt: string;
        user: { id: string; username: string | null; fullName: string | null } | null;
      }>;
    }>(`/inventory/items/${itemId}/history`);
  }

  // ============================================================================
  // PRINT QUEUE (uses print_jobs table)
  // ============================================================================

  async getPrintQueueStats(filters?: { warehouseId?: string; facilityId?: string }) {
    const params = new URLSearchParams();
    if (filters?.warehouseId) params.append('warehouseId', filters.warehouseId);
    if (filters?.facilityId) params.append('facilityId', filters.facilityId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<PrintQueueStats>(`/print-queue/stats${query}`);
  }

  async getPrintJobs(filters?: PrintJobFilters) {
    const params = new URLSearchParams();
    if (filters?.facilityId) params.append('facilityId', filters.facilityId);
    if (filters?.warehouseId) params.append('warehouseId', filters.warehouseId);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.batchId) params.append('batchId', filters.batchId);
    if (filters?.referenceId) params.append('referenceId', filters.referenceId);
    if (filters?.referenceType) params.append('referenceType', filters.referenceType);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset != null) params.append('offset', filters.offset.toString());
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ items: PrintJob[]; total: number }>(`/print-queue${query}`);
  }

  async getPrintJob(id: string) {
    return this.request<PrintJob>(`/print-queue/${id}`);
  }

  async updatePrintJobStatus(id: string, data: { status: string; errorMessage?: string; printedLabels?: number; failedLabels?: number }) {
    return this.request<PrintJob>(`/print-queue/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async cancelPrintJob(id: string) {
    return this.request<PrintJob>(`/print-queue/${id}/cancel`, {
      method: 'POST',
    });
  }

  async getPrintJobLabels(jobId: string) {
    const labels = await this.request<any[]>(`/print-queue/${jobId}/labels`, {
      method: 'GET',
    });
    return labels;
  }

  // ============================================================================
  // DISPOSAL
  // ============================================================================

  async getDisposals(options?: {
    search?: string;
    status?: string;
    statuses?: string[];
    warehouseId?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    const params = new URLSearchParams();
    if (options?.search) params.append('search', options.search);
    if (options?.statuses?.length) params.append('statuses', options.statuses.join(','));
    else if (options?.status) params.append('status', options.status);
    if (options?.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset != null) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ items: any[]; total: number; limit: number; offset: number }>(`/disposal${query}`);
  }

  async getDisposal(id: string) {
    return this.request<any>(`/disposal/${id}`);
  }

  async getDisposableItems(options: {
    warehouseId: string;
    search?: string;
    statusFilter?: 'expired' | 'missing';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    params.append('warehouseId', options.warehouseId);
    if (options.search) params.append('search', options.search);
    if (options.statusFilter) params.append('statusFilter', options.statusFilter);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset != null) params.append('offset', options.offset.toString());
    return this.request<{ products: DisposableProduct[]; total: number; limit: number; offset: number }>(`/disposal/items?${params.toString()}`);
  }

  async createDisposal(data: {
    warehouseId: string;
    itemIds: string[];
    reasonCodeId?: string;
    notes?: string;
  }) {
    return this.request<any>('/disposal', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async assignDisposal(id: string, userId: string) {
    return this.request<any>(`/disposal/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async unassignDisposal(id: string) {
    return this.request<any>(`/disposal/${id}/unassign`, {
      method: 'POST',
    });
  }

  async completeDisposal(id: string) {
    return this.request<{ disposedCount: number; disposal?: unknown }>(`/disposal/${id}/complete`, {
      method: 'POST',
    });
  }

  async confirmDisposal(id: string) {
    return this.request<any>(`/disposal/${id}/confirm`, {
      method: 'POST',
    });
  }

  async rejectDisposal(id: string) {
    return this.request<any>(`/disposal/${id}/reject`, {
      method: 'POST',
    });
  }

  async cancelDisposal(id: string) {
    return this.request<any>(`/disposal/${id}/cancel`, {
      method: 'POST',
    });
  }

  // ============================================================================
  // RELOCATION
  // ============================================================================

  async getRelocations(options?: {
    search?: string;
    status?: string;
    statuses?: string[];
    warehouseId?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: string;
  }) {
    const params = new URLSearchParams();
    if (options?.search) params.append('search', options.search);
    if (options?.statuses?.length) params.append('statuses', options.statuses.join(','));
    else if (options?.status) params.append('status', options.status);
    if (options?.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset != null) params.append('offset', options.offset.toString());
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ items: any[]; total: number; limit: number; offset: number }>(`/relocation${query}`);
  }

  async getRelocation(id: string) {
    return this.request<any>(`/relocation/${id}`);
  }

  async getRelocatableItems(options: {
    warehouseId: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    params.append('warehouseId', options.warehouseId);
    if (options.search) params.append('search', options.search);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset != null) params.append('offset', options.offset.toString());
    return this.request<{ products: DisposableProduct[]; total: number; limit: number; offset: number }>(`/relocation/items?${params.toString()}`);
  }

  async createRelocation(data: {
    warehouseId: string;
    targetLocationId: string;
    itemIds: string[];
    notes?: string;
  }) {
    return this.request<any>('/relocation', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async assignRelocation(id: string, userId: string) {
    return this.request<any>(`/relocation/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async unassignRelocation(id: string) {
    return this.request<any>(`/relocation/${id}/unassign`, {
      method: 'POST',
    });
  }

  async confirmRelocation(id: string) {
    return this.request<any>(`/relocation/${id}/confirm`, {
      method: 'POST',
    });
  }

  async cancelRelocation(id: string) {
    return this.request<any>(`/relocation/${id}/cancel`, {
      method: 'POST',
    });
  }

  async searchLocations(warehouseId: string, query: string, limit?: number) {
    const params = new URLSearchParams();
    params.append('warehouseId', warehouseId);
    params.append('q', query);
    if (limit) params.append('limit', limit.toString());
    return this.request<{ id: string; displayCode: string }[]>(`/locations/search?${params.toString()}`);
  }

  async reprintInventoryItemLabel(inventoryItemId: string) {
    return this.request<{ success: boolean; zpl?: string; error?: string; jobId?: string; labelIdentifier?: string }>(
      `/print-queue/reprint-by-item/${inventoryItemId}`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
    );
  }

  async getLabelPrintHistory(jobId: string, labelIdentifier: string) {
    return this.request<any[]>(`/print-queue/${jobId}/labels/${labelIdentifier}/history`, {
      method: 'GET',
    });
  }

  async confirmLabelPrinted(jobId: string, labelIdentifier: string) {
    return this.request<any>(`/print-queue/${jobId}/labels/${labelIdentifier}/confirm-printed`, {
      method: 'POST',
    });
  }

  async bulkCreateLabelHistory(
    jobId: string,
    entries: Array<{
      inventoryItemId?: string;
      labelIdentifier: string;
      labelIndex?: number;
      status: 'SENT' | 'SUCCESS' | 'FAILED' | 'CANCELLED';
      printerName?: string;
      printerAddress?: string;
      printerPort?: number;
      errorMessage?: string;
      errorCode?: string;
      zplCode?: string;
    }>,
  ) {
    return this.request<any[]>(`/print-queue/${jobId}/labels/history/bulk`, {
      method: 'POST',
      body: JSON.stringify({ entries }),
    });
  }

  // ============================================================================
  // EXPIRY MONITOR
  // ============================================================================

  async getExpirySummary(warehouseId: string) {
    return this.request<ExpirySummary>(`/expiry/summary?warehouseId=${encodeURIComponent(warehouseId)}`);
  }

  // ============================================================================
  // REPORTS
  // ============================================================================

  async getReportInventory(options: {
    warehouseId?: string;
    search?: string;
    includeZeroStock?: boolean;

    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
    export?: boolean;
  }) {
    const params = new URLSearchParams();
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options.search) params.append('search', options.search);
    if (options.includeZeroStock) params.append('includeZeroStock', 'true');

    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.export) {
      params.append('export', 'true');
    } else {
      if (options.limit != null) params.append('limit', options.limit.toString());
      if (options.offset != null) params.append('offset', options.offset.toString());
    }
    return this.request<{ items: ReportInventoryItem[]; total: number }>(`/reports/inventory?${params.toString()}`);
  }

  async getReportMovement(options: {
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    transactionType?: string;
    search?: string;

    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
    export?: boolean;
  }) {
    const params = new URLSearchParams();
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    if (options.transactionType) params.append('transactionType', options.transactionType);
    if (options.search) params.append('search', options.search);

    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.export) {
      params.append('export', 'true');
    } else {
      if (options.limit != null) params.append('limit', options.limit.toString());
      if (options.offset != null) params.append('offset', options.offset.toString());
    }
    return this.request<{ items: ReportMovementItem[]; total: number }>(`/reports/movement?${params.toString()}`);
  }

  async getReportStockTake(options: {
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
    export?: boolean;
  }) {
    const params = new URLSearchParams();
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    if (options.search) params.append('search', options.search);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.export) {
      params.append('export', 'true');
    } else {
      if (options.limit != null) params.append('limit', options.limit.toString());
      if (options.offset != null) params.append('offset', options.offset.toString());
    }
    return this.request<ReportStockTakeData>(`/reports/stocktake?${params.toString()}`);
  }

  async getReportInbound(options: {
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    search?: string;

    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
    export?: boolean;
  }) {
    const params = new URLSearchParams();
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    if (options.status) params.append('status', options.status);
    if (options.search) params.append('search', options.search);

    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.export) {
      params.append('export', 'true');
    } else {
      if (options.limit != null) params.append('limit', options.limit.toString());
      if (options.offset != null) params.append('offset', options.offset.toString());
    }
    return this.request<ReportInboundData>(`/reports/inbound?${params.toString()}`);
  }

  async getReportOutbound(options: {
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    search?: string;

    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
    export?: boolean;
  }) {
    const params = new URLSearchParams();
    if (options.warehouseId) params.append('warehouseId', options.warehouseId);
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    if (options.status) params.append('status', options.status);
    if (options.search) params.append('search', options.search);

    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.export) {
      params.append('export', 'true');
    } else {
      if (options.limit != null) params.append('limit', options.limit.toString());
      if (options.offset != null) params.append('offset', options.offset.toString());
    }
    return this.request<ReportOutboundData>(`/reports/outbound?${params.toString()}`);
  }

  async getExpiryItems(options: {
    warehouseId: string;
    window?: string;
    search?: string;
    sortBy?: string;
    sortDir?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    params.append('warehouseId', options.warehouseId);
    if (options.window) params.append('window', options.window);
    if (options.search) params.append('search', options.search);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortDir) params.append('sortDir', options.sortDir);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset != null) params.append('offset', options.offset.toString());
    return this.request<{ items: ExpiryBatchItem[]; total: number; limit: number; offset: number }>(`/expiry/items?${params.toString()}`);
  }

  // ============================================================================
  // REPORT LINE ITEMS (for detailed exports)
  // ============================================================================

  async getInventoryLineItems(productIds: string[], warehouseId?: string) {
    return this.post<InventoryLineItem[]>('/reports/inventory/line-items', { productIds, warehouseId });
  }

  async getInboundLineItems(orderIds: string[]) {
    return this.post<InboundLineItem[]>('/reports/inbound/line-items', { orderIds });
  }

  async getOutboundLineItems(orderIds: string[]) {
    return this.post<OutboundLineItem[]>('/reports/outbound/line-items', { orderIds });
  }

  async getStockTakeLineItems(stockTakeIds: string[]) {
    return this.post<StockTakeLineItem[]>('/reports/stocktake/line-items', { stockTakeIds });
  }

  // ============================================================================
  // TASKS
  // ============================================================================

  async getTasks(params: URLSearchParams) {
    return this.get<{ items: any[]; total: number; limit: number; offset: number }>(`/tasks?${params.toString()}`);
  }

  async getTaskScannedItems(taskId: string) {
    return this.get<any[]>(`/tasks/${taskId}/scanned-items`);
  }

  // ============================================================================
  // PUTAWAY
  // ============================================================================

  async cancelPutawayTask(taskId: string) {
    return this.post(`/inventory/workflow/putaway/tasks/${taskId}/cancel`);
  }

  // ============================================================================
  // OUTBOUND ORDER CANCEL
  // ============================================================================

  async cancelOutboundOrder(id: string, finalizeCancel = true) {
    const query = finalizeCancel ? '?finalizeCancel=true' : '';
    return this.post(`/outbound-orders/${id}/cancel${query}`);
  }

  async resumePicking(orderId: string) {
    return this.post(`/outbound-orders/${orderId}/resume-picking`);
  }

  async cancelRemainingItems(orderId: string) {
    return this.post(`/outbound-orders/${orderId}/cancel-remaining`);
  }

  // ============================================================================
  // LOCATION MANAGEMENT
  // ============================================================================

  async updateLocation(id: string, data: any) {
    return this.put(`/locations/${id}`, data);
  }

  async renameLocationSegment(data: any) {
    return this.put('/locations/rename-segment', data);
  }

  async generateLocationsDynamic(config: any) {
    return this.post<{ created: number; conflicts: number; conflictCodes: string[]; message: string }>('/locations/batch-create-dynamic', config);
  }

  // ============================================================================
  // WAREHOUSE DELETE
  // ============================================================================

  async deleteWarehouse(id: string) {
    return this.delete(`/warehouses/${id}`);
  }

  // ============================================================================
  // ORGANIZATION SETTINGS
  // ============================================================================

  async updateLocationDisplaySettings(configs: any) {
    return this.put('/organizations/settings/location-display', configs);
  }

  // ============================================================================
  // INBOUND ORDER UPDATE
  // ============================================================================

  async updateInboundOrder(id: string, data: any) {
    return this.put(`/inbound-orders/${id}`, data);
  }
}

// ============================================================================
// PRINT QUEUE TYPES (Exported for reuse across components)
// Maps to: print_jobs table in PostgreSQL
// ============================================================================

export type PrintJobType = 'PRODUCTION_LABEL' | 'INBOUND_LABEL' | 'LOCATION_LABEL' | 'CONTAINER_LABEL' | 'SHIPPING_LABEL';
export type PrintJobStatus = 'PENDING' | 'PRINTING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'EXPIRED';
export type PrintJobPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
export type LabelFormat = 'ZPL' | 'EPL' | 'PDF' | 'PNG';

export interface DisposableItemLocation {
  zone: string | null;
  aisle: string | null;
  rack: string | null;
  level: string | null;
  bin: string | null;
}

export interface DisposableItem {
  id: string;
  epc: string | null;
  serialNumber: string | null;
  status: string;
  location: DisposableItemLocation | null;
}

export interface DisposableBatch {
  batchId: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  itemCount: number;
  items: DisposableItem[];
}

export interface DisposableProduct {
  productId: string | null;
  catalogNumber: string | null;
  productName: string | null;
  totalItemCount: number;
  batches: DisposableBatch[];
}

export interface PrintQueueStats {
  pending: number;
  printing: number;
  completed: number;
  failed: number;
  cancelled: number;
  expired: number;
  totalToday: number;
  byType: Record<string, number>;
  byPriority: Record<string, number>;
}

export interface PrintJob {
  id: string;
  organizationId: string;
  facilityId: string | null;
  facility?: { id: string; name: string; code: string } | null;
  warehouseId: string | null;
  warehouse?: { id: string; name: string; code: string } | null;
  type: PrintJobType;
  status: PrintJobStatus;
  priority: PrintJobPriority;
  priorityOrder: number;
  batchId: string | null;
  batchSequence: number | null;
  title: string;
  description: string | null;
  notes: string | null;
  referenceId: string | null;
  referenceType: string | null;
  referenceNumber: string | null;
  totalLabels: number;
  copiesPerLabel: number;
  printedLabels: number;
  failedLabels: number;
  printerId: string | null;
  printerAddress: string | null;
  printerName: string | null;
  printerPort: number;
  templateId: string | null;
  labelWidthMm: number | null;
  labelHeightMm: number | null;
  labelFormat: LabelFormat;
  jobData: Record<string, any>;
  errorMessage: string | null;
  errorCode: string | null;
  lastErrorAt: string | null;
  retryCount: number;
  maxRetries: number;
  retryDelaySeconds: number;
  nextRetryAt: string | null;
  createdBy: string;
  creator?: { id: string; fullName: string; email: string } | null;
  processedBy: string | null;
  processor?: { id: string; fullName: string; email: string } | null;
  cancelledBy: string | null;
  canceller?: { id: string; fullName: string; email: string } | null;
  cancellationReason: string | null;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface PrintJobFilters {
  facilityId?: string;
  warehouseId?: string;
  type?: string;
  status?: string;
  priority?: string;
  batchId?: string;
  referenceId?: string;
  referenceType?: string;
  search?: string;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreatePrintJobDto {
  type: PrintJobType;
  title: string;
  description?: string;
  notes?: string;
  facilityId?: string;
  warehouseId?: string;
  referenceId?: string;
  referenceType?: string;
  totalLabels?: number;
  copiesPerLabel?: number;
  printerId?: string;
  printerAddress?: string;
  printerName?: string;
  printerPort?: number;
  templateId?: string;
  labelWidthMm?: number;
  labelHeightMm?: number;
  labelFormat?: LabelFormat;
  priority?: PrintJobPriority;
  jobData?: Record<string, any>;
  scheduledAt?: string;
  expiresAt?: string;
  maxRetries?: number;
  retryDelaySeconds?: number;
}

// Product Import Types
export interface ProductImportFieldOption {
  value: string;
  label: string;
  aliases: string[];
}

export interface ProductImportFieldConfig {
  field: string;
  label: string;
  required: boolean;
  type: 'string' | 'number' | 'boolean' | 'enum';
  options?: ProductImportFieldOption[];
  aliases: string[];
  hint?: string;
}

export interface ProductImportParseResult {
  fileId: string;
  headers: string[];
  sampleRows: Record<string, string>[];
  totalRows: number;
  suggestedMappings: Record<string, string>;
}

export interface ProductImportValidationError {
  field: string;
  message: string;
}

export interface ProductImportValidatedRow {
  rowNumber: number;
  data: Record<string, any>;
}

export interface ProductImportInvalidRow {
  rowNumber: number;
  data: Record<string, string>;
  errors: ProductImportValidationError[];
}

export interface ProductImportSkippedRow {
  rowNumber: number;
  catalogNumber: string;
  reason: string;
}

export interface ProductImportValidationResult {
  valid: ProductImportValidatedRow[];
  invalid: ProductImportInvalidRow[];
  skipped: ProductImportSkippedRow[];
  summary: {
    total: number;
    valid: number;
    invalid: number;
    skipped: number;
  };
}

export interface ProductImportResult {
  imported: number;
  skipped: number;
  errors: string[];
}

export interface ProductImportTemplate {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  columnMappings: Record<string, string>;
  defaultValues?: Record<string, any>;
  valueTransformations?: Record<string, Record<string, string>>;
  isDefault: boolean;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

// Stock Take type (uses stock_takes table)
// Schema: id, organization_id, warehouse_id, reference_no, status,
//         target_location_id, assigned_to_user_id, started_at, completed_at, created_at
interface StockTakeTask {
  id: string;
  referenceNo: string;
  status: 'PLANNED' | 'ASSIGNED' | 'IN_PROGRESS' | 'REVIEW' | 'COMPLETED' | 'CANCELLED';
  targetLocation?: { id: string; zone?: string | null; aisle?: string | null; rack?: string | null; level?: string | null; bin?: string | null } | null;
  warehouse?: { id: string; name: string; code: string } | null;
  assignedToUser?: { id: string; fullName: string; email: string } | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  reference?: string | null;
  note?: string | null;
  // Computed counts
  countedItemCount?: number;
  expectedItemCount?: number;
  scans?: any[];
}

// Expiry Monitor Types
export interface ExpirySummary {
  expired: number;
  total: number;
}

export interface ExpiryBatchItem {
  batchId: string;
  batchNumber: string;
  expiryDate: string;
  daysRemaining: number;
  productId: string;
  productName: string;
  catalogNumber: string | null;
  itemCount: number;
  locationId: string | null;
  zone: string | null;
  aisle: string | null;
  rack: string | null;
  level: string | null;
  bin: string | null;
}

// Report Types
export interface ReportInventoryItem {
  productId: string;
  catalogNumber: string;
  name: string;
  category: string | null;
  totalOnHand: number;
  received: number;
  available: number;
  allocated: number;
  agingBrackets: {
    days0to30: number;
    days31to60: number;
    days61to90: number;
    days90plus: number;
  };
  lowStockAlert: boolean;
  reorderPoint: number;
}

export interface ReportMovementItem {
  transactionType: string;
  timestamp: string;
  catalogNumber: string | null;
  productName: string | null;
  quantityChange: string;
  fromLocationId: string | null;
  toLocationId: string | null;
  userName: string | null;
  notes: string | null;
}

export interface ReportStockTakeItem {
  id: string;
  referenceNo: string;
  completedAt: string;
  warehouseName: string | null;
  totalExpected: number;
  totalCounted: number;
  totalVariance: number;
}

export interface ReportStockTakeData {
  items: ReportStockTakeItem[];
  total: number;
  summary: {
    totalStockTakes: number;
    itemsWithVariance: number;
    netVariance: number;
  };
}

export interface ReportInboundItem {
  id: string;
  orderNumber: string;
  referenceNumber: string | null;
  status: string;
  inboundType: string;
  supplierName: string | null;
  warehouseName: string | null;
  expectedArrivalDate: string | null;
  actualArrivalDate: string | null;
  createdAt: string;
  totalExpected: number;
  totalReceived: number;
  lineCount: number;
}

export interface ReportInboundData {
  items: ReportInboundItem[];
  total: number;
  summary: {
    total: number;
    byStatus: Record<string, number>;
  };
}

export interface ReportOutboundItem {
  id: string;
  orderNumber: string;
  referenceNumber: string | null;
  status: string;
  customerName: string | null;
  warehouseName: string | null;
  carrierName: string | null;
  trackingNumber: string | null;
  requestedDeliveryDate: string | null;
  createdAt: string;
  totalOrdered: number;
  totalPicked: number;
  lineCount: number;
}

export interface ReportOutboundData {
  items: ReportOutboundItem[];
  total: number;
  summary: {
    total: number;
    byStatus: Record<string, number>;
  };
}

// Line-item types for detailed report exports
export interface InventoryLineItem {
  productId: string;
  epc: string;
  serialNumber: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  locationName: string;
  status: string;
  quantity: string;
}

export interface InboundLineItem {
  inboundOrderId: string;
  catalogNumber: string | null;
  productName: string | null;
  uom: string;
  quantityExpected: number;
  quantityReceived: number;
  batchNo: string | null;
  expiryDate: string | null;
}

export interface OutboundLineItem {
  outboundOrderId: string;
  catalogNumber: string | null;
  productName: string | null;
  uom: string;
  quantityOrdered: number;
  quantityPicked: number;
}

export interface StockTakeLineItem {
  stockTakeId: string;
  catalogNumber: string | null;
  productName: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  systemQty: number;
  countedQty: number;
  variance: number;
}

export const api = new ApiClient(API_BASE_URL);
